<template>
  <div
    class="mega-nav"
    @mouseover="handleMouseOver"
  >
    <div
      v-for="tier1 of mapNavigation"
      :key="tier1.handle"
      class="mega-nav__panel"
      :class="{
        'active': activeMenu === tier1.handle,
        'single-link': !tier1.links.length,
      }"
      @mouseleave="handleCloseNav"
    >
      <div class="mega-nav__inner">
        <div class="mega-nav__menus">
          <ul class="mega-nav__menu">
            <li
              v-for="tier2 of tier1.links"
              :key="tier2.handle"
              class="mega-nav__menu-item"
            >
              <a
                v-if="tier2.url !== '#'"
                :href="tier2.url"
                class="mega-nav__link"
                :class="handleUnboldColumnHeaders(tier2.title)"
              >
                {{ tier2.title }}
              </a>

              <span
                v-else
                class="mega-nav__link"
                :class="handleUnboldColumnHeaders(tier2.title)"
              >
                {{ tier2.title }}
              </span>

              <ul class="mega-nav__sub-menu">
                <li
                  v-for="tier3 of tier2.links"
                  :key="tier3.handle"
                  class="mega-nav__menu-sub-item"
                >
                  <a
                    class="mega-nav__link"
                    :href="tier3.url"
                  >
                    {{ tier3.title }}
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div
          v-if="getSignposts(tier1.title).length"
          class="mega-nav__signposts"
        >
          <div
            v-for="signpost, index of getSignposts(tier1.title)"
            :key="index"
            class="mega-nav__signpost"
          >
            <a
              v-if="checkSignpostLink(signpost)"
              class="mega-nav__signpost-link"
              :href="signpost.link"
            >
              <div class="mega-nav__image-wrap">
                <responsive-image
                  :image="signpost.image"
                  ratio="1:1"
                />
              </div>

              <span v-text="signpost.cta_copy" />
            </a>

            <div
              v-else
              class="mega-nav__image-wrap"
            >
              <responsive-image
                :image="signpost.image"
                ratio="1:1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Mega nav (mega-nav)
 * -----------------------------------------------------------------------------
 * Mega nav pane for desktop navigation links.
 *
 * @param {String} activeMenu - Handle of active parent link.
 * @param {Object} navigation - Collection of links to render.
 * @param {Object} signposts - Featured images and links for panels.
 * @param {String} unboldColumnHeaders - Names of any subheadings to unbold.
 *
 */

import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'MegaNav',
  components: {
    ResponsiveImage,
  },
  props: {
    activeMenu: {
      type: String,
      default: '',
    },
    navigation: {
      type: Object,
      default: () => ({}),
    },
    signposts: {
      type: Object,
      default: () => ({}),
    },
    unboldColumnHeaders: {
      type: String,
      default: '',
    },
  },

  emits: ['clear-active-menu', 'enter-mega-menu'],

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Filter navigation so max two link lists show per mega nav panel.
     * @returns {Array}
     */
    mapNavigation() {
      const mapped = this.navigation.map((parentLink) => {
        parentLink.links = parentLink.links.slice(0, 2)

        return parentLink
      })

      return mapped
    },
  },

  methods: {

    /**
     * Get signposts by link title.
     * @param {String} title - Tier 1 link title.
     * @returns {Array}
     */
    getSignposts(title) {
      const found = this.signposts.filter((signpost) => signpost.key === title).slice(0, 3)

      return found
    },

    /**
     * Check signposts have CTA text and link.
     * @param {Object} signpost - Signpost object.
     * @returns {Boolean}
     */
    checkSignpostLink(signpost) {
      const { cta_copy, link } = signpost

      // eslint-disable-next-line camelcase
      if (cta_copy !== '' && link !== null) {
        return true
      }

      return false
    },

    /**
     * Handle mouseout event on mega nav panels.
     */
    handleCloseNav() {
      this.$emit('clear-active-menu')
    },

    /**
     * Handle mouseover event on mega nav and all children.
     */
    handleMouseOver() {
      this.$emit('enter-mega-menu')
    },

    /**
     * Unbold column header if it is in list of exclusions.
     * @param {String} title - Link title to check against exclusions.
     * @returns {String}
     */
    handleUnboldColumnHeaders(title) {
      if (!this.unboldColumnHeaders.length) {
        return 'column-header'
      }

      const wordsArray = this.unboldColumnHeaders.split(',')
      const arrayClean = wordsArray.map((word) => {
        return word.toLowerCase().trim()
      })

      let unbold = false

      arrayClean.forEach((word) => {
        if (word !== title.toLowerCase()) {
          return
        }

        unbold = true
      })

      if (unbold) {
        return ''
      }

      return 'column-header'
    },
  },
}
</script>
